import { useCallback } from 'react';
import queryString from 'query-string';
import { useRouter } from '@/hooks';

interface UrlParams {
  assembly?: string;
  overviewTab?: string;
  poi?: string;
  /** Kebab-case POI template tab id in review panel (matches `toKebabCase(template.name)`). Omitted = All. */
  reviewPoiTab?: string;
  /** Image ID currently displayed in the review panel image preview drawer. */
  reviewImage?: string;
  [key: string]: string | string[] | undefined;
}

export const useInsightsPageParams = () => {
  const router = useRouter();

  const getCurrentParams = useCallback((): UrlParams => {
    return queryString.parse(window.location.search, { arrayFormat: 'comma' }) as UrlParams;
  }, []);

  const updateParams = useCallback(
    (newParams: Partial<UrlParams>) => {
      const currentParams = getCurrentParams();
      const updatedParams = { ...currentParams, ...newParams };

      router.push({
        pathname: router.pathname,
        query: updatedParams as Record<string, string>,
      });
    },
    [router, getCurrentParams]
  );

  const replaceParams = useCallback(
    (newParams: Partial<UrlParams>) => {
      const currentParams = getCurrentParams();
      const updatedParams = { ...currentParams, ...newParams };

      router.replace({
        pathname: router.pathname,
        query: updatedParams as Record<string, string>,
      });
    },
    [router, getCurrentParams]
  );

  const removeParams = useCallback(
    (paramsToRemove: string[]) => {
      const currentParams = getCurrentParams();
      const updatedParams = { ...currentParams };

      paramsToRemove.forEach((param) => {
        delete updatedParams[param];
      });

      router.push({
        pathname: router.pathname,
        query: updatedParams as Record<string, string>,
      });
    },
    [router, getCurrentParams]
  );

  const setAssembly = useCallback(
    (assembly: string, overviewTab: string = '1') => {
      updateParams({ assembly, overviewTab });
    },
    [updateParams]
  );

  const setOverviewTab = useCallback(
    (overviewTab: string) => {
      updateParams({ overviewTab });
    },
    [updateParams]
  );

  const setPoi = useCallback(
    (poi: string) => {
      const currentParams = getCurrentParams();
      const updatedParams = { ...currentParams, poi };
      delete updatedParams['reviewImage'];
      router.push({
        pathname: router.pathname,
        query: updatedParams as Record<string, string>,
      });
    },
    [router, getCurrentParams]
  );

  const setReviewPoiTab = useCallback(
    (tabKebabId: string | undefined) => {
      if (!tabKebabId) {
        removeParams(['reviewPoiTab']);
      } else {
        updateParams({ reviewPoiTab: tabKebabId });
      }
    },
    [updateParams, removeParams]
  );

  const setReviewImage = useCallback(
    (imageId: string) => {
      replaceParams({ reviewImage: imageId });
    },
    [replaceParams]
  );

  const clearAssembly = useCallback(() => {
    removeParams([
      'selectedAssemblyName',
      'overviewTab',
      'selectedAssemblyId',
      'assembly',
      'reviewPoiTab',
    ]);
  }, [removeParams]);

  const clearPoi = useCallback(() => {
    removeParams(['poi', 'reviewImage']);
  }, [removeParams]);

  return {
    getCurrentParams,
    updateParams,
    replaceParams,
    removeParams,
    setAssembly,
    setOverviewTab,
    setPoi,
    setReviewPoiTab,
    setReviewImage,
    clearAssembly,
    clearPoi,
  };
};
